// ─────────────────────────────────────────
//  Slumpad ordning per grupp (presentationslager)
// ─────────────────────────────────────────
// Servern lagrar gruppens permutation på group_results.shuffle:
//
//   { q: [3,1,0,…], o: [[2,0,3,1], …] }
//     q[visningsposition]      = kanoniskt frågeindex
//     o[kanonisk fråga][pos]   = kanoniskt alternativindex
//
// Allt utanför PlayScreen — poäng, scratch_state, appeals, burning questions,
// dashboarden — arbetar i kanonisk ordning. Här sker hela översättningen.
//
// En permutation som inte är hel och giltig ignoreras: hellre kanonisk ordning
// för alla än en vy där ett alternativ saknas eller visas två gånger.

// Identitetsordningen [0, 1, 2, … n-1]
function identity(n) {
  return Array.from({ length: n }, (_, i) => i);
}

// Är perm en fullständig permutation av 0…n-1?
function isPermutation(perm, n) {
  if (!Array.isArray(perm) || perm.length !== n) return false;
  const seen = new Set();
  for (const v of perm) {
    if (!Number.isInteger(v) || v < 0 || v >= n || seen.has(v)) return false;
    seen.add(v);
  }
  return true;
}

// Frågornas visningsordning: order[visningsposition] = kanoniskt frågeindex
function questionOrder(shuffle, numQ) {
  const perm = shuffle?.q;
  return isPermutation(perm, numQ) ? perm : identity(numQ);
}

// Alternativens visningsordning för EN fråga (kanoniskt index qi):
// order[visningsposition] = kanoniskt alternativindex
function optionOrder(shuffle, qi, numOptions) {
  const perm = Array.isArray(shuffle?.o) ? shuffle.o[qi] : null;
  return isPermutation(perm, numOptions) ? perm : identity(numOptions);
}

// Vänder på en ordning: inverse(order)[kanoniskt index] = visningsposition.
// Används för att visa serverns svar (t.ex. "rätt alternativ var 2") med den
// bokstav gruppen faktiskt såg.
function inverseOrder(order) {
  const inv = new Array(order.length);
  order.forEach((canonical, pos) => { inv[canonical] = pos; });
  return inv;
}

// Slumpen förutsätter att gruppen ser det som kastas om. Står frågorna på
// papper och bara facit ligger i appen finns inget att känna igen: ett slumpat
// "C" i appen är då inte pappersbladets C, och gruppen rättas mot fel
// alternativ — likaså är appens "Q1" inte pappersbladets fråga 1. Därför
// gäller slumpen bara det som faktiskt syns i appen:
//   - alternativen slumpas för en fråga bara om den har alternativtexter
//   - frågorna slumpas bara om varenda fråga går att känna igen i appen
// Allt i kanonisk ordning annars, precis som när slumpen är avslagen.
function hasOptionTexts(q, labels) {
  return labels.some(lbl => q?.options?.[lbl]?.trim());
}
function isRecognizable(q, labels) {
  return !!(q?.text?.trim()
    || (Array.isArray(q?.images) && q.images.length > 0)
    || hasOptionTexts(q, labels));
}
function visibleShuffle(shuffle, questions, numQ, labels) {
  if (!shuffle) return null;
  const qs = Array.from({ length: numQ }, (_, i) => (questions || [])[i]);
  const out = {};
  if (shuffle.q && qs.every(q => isRecognizable(q, labels))) out.q = shuffle.q;
  if (Array.isArray(shuffle.o)) {
    out.o = qs.map((q, qi) => (hasOptionTexts(q, labels) ? shuffle.o[qi] : null));
  }
  return out;
}

export { questionOrder, optionOrder, inverseOrder, isPermutation, identity, visibleShuffle };
